import { inject, Injectable, signal } from '@angular/core';
import { LoginRequest, SignInResponse } from 'app/core/auth/auth.types';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { BaseAdminService } from 'app/core/common/services/base-admin.service';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ApiResponse } from '../common/interfaces/api.interface';
import { LoggedInUser, User } from '../common/models';
import { LocalStorageService } from '../localStorage/localStorage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _baseAdminService = inject(BaseAdminService);
  private _localStorageService = inject(LocalStorageService);
  private _currentUser = signal<LoggedInUser | null>(null);
  private _jwt = signal<string | null>(null);

  get currentUser(): LoggedInUser {
    return this._currentUser();
  }

  set currentUser(value: LoggedInUser) {
    this._currentUser.set(value);
  }

  set accessToken(token: string) {
    this._jwt.set(token);

    if (token) {
      this._localStorageService.setItem('accessToken', token);
    } else {
      this._localStorageService.removeItem('accessToken');
    }
  }

  get accessToken(): string {
    return this._jwt() ?? this._localStorageService.getItem('accessToken');
  }

  getMe(): Observable<ApiResponse<{ user: User }>> {
    return this._baseAdminService.get<{ user: User }>('/users/me').pipe(
      tap((response) => {
        const { user } = response.data;

        this.currentUser = {
          _id: user._id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          role: user.role,
        };
      }),
    );
  }

  signIn(credentials: LoginRequest): Observable<SignInResponse> {
    // Throw error, if the user is already logged in
    if (this.currentUser) {
      return throwError(() => 'User is already logged in.');
    }

    return this._baseAdminService.post<SignInResponse>('/auth/login', credentials).pipe(
      switchMap((response) => {
        const { accessToken, user } = response.data;

        const convertedUser: LoggedInUser = {
          _id: user._id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          role: user.role,
        };

        this.accessToken = accessToken;
        this.currentUser = convertedUser;

        return of({ accessToken, user });
      }),
    );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Clear the user data
    this.currentUser = null;
    this.accessToken = null;

    // Return the observable
    return of(true);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this.currentUser) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    return of(true);
  }
}
